import { useState } from "react";
import {
  Alert,
  Button,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import type {
  CustomerRole,
  Organisation,
  OrganisationId,
  UserMembership,
} from "@thalamos/haven-common";
import { CustomerRoleSchema } from "@thalamos/haven-common";

import {
  OrganisationPicker,
  type OrganisationSearch,
} from "../OrganisationPicker/OrganisationPicker.js";

/**
 * Whether a grant or a revocation landed.
 *
 * One `failed` for a server that refused and one that could not be reached, for
 * the reason `ProfileSaveOutcome` gives: nothing the person at the screen could
 * do differently tells the two apart.
 */
export type MembershipChangeOutcome = "changed" | "failed";

/**
 * The words each section of memberships is shown with. Case work and admin are
 * granted the same way but are different decisions, so each says what it is.
 */
const COPY = {
  "case-work": {
    title: "admin.memberships.title",
    description: "admin.memberships.description",
    none: "admin.memberships.none",
    grant: "admin.memberships.grant",
    alreadyHeld: "admin.memberships.alreadyHeld",
  },
  admin: {
    title: "admin.adminMemberships.title",
    description: "admin.adminMemberships.description",
    none: "admin.adminMemberships.none",
    grant: "admin.adminMemberships.grant",
    alreadyHeld: "admin.adminMemberships.alreadyHeld",
  },
} as const;

export type MembershipSection = keyof typeof COPY;

const isHeld = (
  memberships: readonly UserMembership[],
  organisationId: OrganisationId | undefined,
  role: CustomerRole,
): boolean =>
  memberships.some(
    (membership) =>
      membership.organisationId === organisationId && membership.role === role,
  );

/**
 * One section of what an account may act as, and the controls for changing it.
 *
 * Separate from `UserProfileForm` because it edits a different thing: that one
 * changes what an account says about a person, this one changes what they may
 * do. Keeping them apart is also what lets the page show one without the other,
 * which is the state an administrator who may rename somebody but not admit them
 * anywhere would be in.
 *
 * Everything is injected rather than fetched here, so mounting it needs no
 * session, and no router unless `organisationPath` is given — a story or a test
 * can show an account with no memberships, a list that failed to load, or a
 * grant that was refused.
 */
export const UserMembershipsPanel = ({
  section,
  grantable,
  memberships,
  loading,
  unavailable,
  organisations,
  organisationSearch,
  organisationPath,
  onGrant,
  onRevoke,
}: {
  section: MembershipSection;
  /**
   * The roles this section may grant, in the order they are offered. With one,
   * there is nothing to choose, so neither the picker nor a role column shows.
   */
  grantable: readonly CustomerRole[];
  /** What the account holds that belongs in this section. */
  memberships: readonly UserMembership[];
  loading: boolean;
  /** The list could not be read. Distinct from an account that holds none. */
  unavailable: boolean;
  /** The organisations a grant may name; narrowed by `organisationSearch` when one is given. */
  organisations: readonly Organisation[];
  organisationSearch?: OrganisationSearch | undefined;
  /** Where each organisation's name links to; without one, it is plain text. */
  organisationPath?: ((organisationId: OrganisationId) => string) | undefined;
  onGrant: (
    organisationId: OrganisationId,
    role: CustomerRole,
  ) => Promise<MembershipChangeOutcome>;
  /** Without one, no row offers a way to remove it. */
  onRevoke?:
    | ((
        organisationId: OrganisationId,
        role: CustomerRole,
      ) => Promise<MembershipChangeOutcome>)
    | undefined;
}) => {
  const { t } = useTranslation();
  const copy = COPY[section];
  const choosesARole = grantable.length > 1;

  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [role, setRole] = useState<CustomerRole | undefined>(grantable[0]);
  const [changing, setChanging] = useState(false);
  const [failed, setFailed] = useState(false);

  // No form library and no schema: both inputs choose from a closed set, so
  // there is no free text for a schema to have an opinion about. What would be
  // validation is the button's own condition.
  const alreadyHeld =
    role !== undefined && isHeld(memberships, organisation?.id, role);
  const canGrant =
    organisation !== null && role !== undefined && !alreadyHeld && !changing;

  const change = async (
    apply: () => Promise<MembershipChangeOutcome>,
  ): Promise<void> => {
    setChanging(true);
    // Through `then`, so a callback that throws before it returns a promise
    // fails the change rather than leaving the panel changing.
    const outcome = await Promise.resolve()
      .then(apply)
      .catch((): MembershipChangeOutcome => "failed");
    setFailed(outcome === "failed");
    setChanging(false);
  };

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={`memberships-${section}`}
      sx={{ p: 3 }}
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h6" component="h2" id={`memberships-${section}`}>
            {t(copy.title)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(copy.description)}
          </Typography>
        </Stack>

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            {t("common.loading")}
          </Typography>
        ) : unavailable ? (
          // Said differently from an account that holds none, because an empty
          // list is a fact about the account and this is a fact about the
          // request. Rendering "holds nothing" for a failed read would tell an
          // administrator something untrue about somebody's access.
          <Alert severity="error">{t("admin.memberships.loadFailed")}</Alert>
        ) : memberships.length === 0 ? (
          <Alert severity="info">{t(copy.none)}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.memberships.organisation")}</TableCell>
                {choosesARole ? (
                  <TableCell>{t("admin.memberships.role")}</TableCell>
                ) : null}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {memberships.map((membership) => (
                <MembershipRow
                  // Both halves, because a person may hold two roles at one
                  // organisation — the organisation alone would collide.
                  key={`${membership.organisationId}:${membership.role}`}
                  membership={membership}
                  showsRole={choosesARole}
                  organisationPath={organisationPath}
                  disabled={changing}
                  onRevoke={onRevoke}
                  change={change}
                />
              ))}
            </TableBody>
          </Table>
        )}

        {failed ? (
          <Alert severity="error">{t("admin.memberships.changeFailed")}</Alert>
        ) : null}

        <Stack spacing={2}>
          <Typography variant="subtitle2" component="h3">
            {t(copy.grant)}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <OrganisationPicker
              label={t("admin.memberships.organisation")}
              organisations={organisations}
              search={organisationSearch}
              value={organisation}
              onChange={setOrganisation}
              sx={{ minWidth: 260, flexGrow: 1 }}
            />

            {choosesARole ? (
              <TextField
                select
                id={`membership-role-${section}`}
                label={t("admin.memberships.role")}
                value={role ?? ""}
                size="small"
                sx={{ minWidth: 180 }}
                onChange={(event) => {
                  // The options are this schema's own, so a value it rejects
                  // cannot come from the select.
                  setRole(CustomerRoleSchema.parse(event.target.value));
                }}
              >
                {grantable.map((offered) => (
                  <MenuItem key={offered} value={offered}>
                    {t(`roles.${offered}`)}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}

            <Button
              variant="contained"
              disabled={!canGrant}
              sx={{ alignSelf: { sm: "center" } }}
              onClick={() => {
                if (organisation === null || role === undefined) {
                  return;
                }
                void change(() => onGrant(organisation.id, role));
              }}
            >
              {t("admin.memberships.grantAction")}
            </Button>
          </Stack>

          {alreadyHeld ? (
            <Typography variant="body2" color="text.secondary">
              {t(copy.alreadyHeld)}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * One membership, with the control that takes it away where one is given, or
 * the reason it cannot be removed here.
 *
 * Every row is disabled while any change is in flight, deliberately: the change
 * is followed by a re-read of the whole set, so acting on a second row before it
 * lands would be acting on a list that is about to be replaced.
 */
const MembershipRow = ({
  membership,
  showsRole,
  organisationPath,
  disabled,
  onRevoke,
  change,
}: {
  membership: UserMembership;
  showsRole: boolean;
  organisationPath: ((organisationId: OrganisationId) => string) | undefined;
  disabled: boolean;
  onRevoke:
    | ((
        organisationId: OrganisationId,
        role: CustomerRole,
      ) => Promise<MembershipChangeOutcome>)
    | undefined;
  change: (apply: () => Promise<MembershipChangeOutcome>) => Promise<void>;
}) => {
  const { t } = useTranslation();

  // `Role` includes Thalamos's own administrative membership, which the
  // endpoint does not accept, so its row is shown and cannot be removed here:
  // who holds it is settled by the directory group.
  const grantable = CustomerRoleSchema.safeParse(membership.role);
  const organisationName =
    membership.organisationName ?? membership.organisationId;

  const action = !grantable.success ? (
    <Typography variant="body2" color="text.secondary">
      {t("admin.memberships.reconciled")}
    </Typography>
  ) : onRevoke === undefined ? null : (
    <Button
      size="small"
      color="error"
      disabled={disabled}
      onClick={() => {
        void change(() => onRevoke(membership.organisationId, grantable.data));
      }}
    >
      {t("admin.memberships.revokeAction")}
    </Button>
  );

  return (
    <TableRow>
      <TableCell>
        {organisationPath === undefined ? (
          organisationName
        ) : (
          <Link
            component={RouterLink}
            to={organisationPath(membership.organisationId)}
          >
            {organisationName}
          </Link>
        )}
      </TableCell>
      {showsRole ? (
        <TableCell>{t(`roles.${membership.role}`)}</TableCell>
      ) : null}
      <TableCell align="right">{action}</TableCell>
    </TableRow>
  );
};
